import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';

import { useSiteConfig } from '../../contexts/SiteConfigContext';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

// WhatsApp Link Component
const WhatsAppLink: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => {
  const { currentLanguage } = useLanguage();
  const { config } = useSiteConfig();

  // WhatsApp numbers based on language
  const whatsappNumbers = {
    en: config.phoneNumberTony, // Tony
    id: config.phoneNumberNila  // Nila
  };

  // Default messages based on language
  const defaultMessages = {
    en: config.whatsappMessageTonyEn,
    id: config.whatsappMessageNilaId
  };

  const phoneNumber = whatsappNumbers[currentLanguage as keyof typeof whatsappNumbers] || whatsappNumbers.en;
  const message = defaultMessages[currentLanguage as keyof typeof defaultMessages] || defaultMessages.en;
  const encodedMessage = encodeURIComponent(message);

  const handleClick = () => {
    // Track WhatsApp click
    if (window.gtag) {
      window.gtag('event', 'whatsapp_click', {
        event_category: 'Contact',
        event_label: `WhatsApp ${currentLanguage.toUpperCase()}`,
      });
    }
  };

  return (
    <a
      href={`https://wa.me/${phoneNumber.replace(/\D/g, '')}?text=${encodedMessage}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={className}
    >
      {children}
    </a>
  );
};

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [isScrolled, setIsScrolled] = useState(false);
  const isForBusinessPage = location.pathname === '/for-business';

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.portfolio'), href: '/portfolio' },
    { name: t('nav.reviews'), href: '/reviews' },
    { name: t('nav.forBusiness'), href: '/for-business' },
    { name: t('nav.charity'), href: '/charity' },
    // { name: t('nav.contact'), href: '/contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Function to send tracking event to Google Analytics
  const trackNavigationClick = (label: string) => {
    if (window.gtag) {
      window.gtag('event', 'navigation_click', {
        event_category: 'Navigation',
        event_label: label,
      });
    }
  };

  const headerClasses = () => {
    if (isForBusinessPage) {
      return 'bg-white shadow-md';
    }
    if (isScrolled) {
      return 'backdrop-blur-premium border-b border-white/20';
    }
    return 'bg-gradient-to-r from-red-900/8 via-red-800/6 to-pink-900/8 backdrop-blur-md border-b border-white/20';
  };

  const navLinkClasses = (href: string) => {
    const active = isActive(href);
    if (isForBusinessPage) {
      return `relative px-4 py-2 text-base font-semibold transition-colors duration-300 rounded-full ${
        active
          ? 'text-burgundy-700'
          : 'text-gray-800 hover:text-[#7a3255]'
      }`;
    }
    return `relative px-4 py-2 text-base font-semibold transition-colors duration-300 rounded-full ${
      active
        ? 'text-[#773255]'
        : 'text-gray-700 hover:text-[#7a3255]'
    }`;
  };

  return (
    <header className={`relative w-full transition-all duration-300 ${headerClasses()} py-3`}>
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center lg:justify-between justify-center">
          {/* Logo - Left */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center group">
              <img
                src="/logo-optimized.webp"
                srcSet="/logo-optimized.webp 1x, /logo-optimized@2x.webp 2x"
                alt="TN Cakes Bali"
                width={210}
                height={68}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                style={{ 
                  objectFit: 'contain',
                  transform: 'translateZ(0)',
                  willChange: 'transform'
                }}
              />
            </Link>
          </div>

          {/* Desktop Navigation - Center */}
          <div className="hidden lg:flex">
            <nav className="flex items-center space-x-10">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => trackNavigationClick(item.name)}
                  className={navLinkClasses(item.href)}
                >
                  {item.name}
                  {isActive(item.href) && !isForBusinessPage && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-[#773255]/10 rounded-full -z-10"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              ))}
            </nav>
          </div>

          {/* CTA - Right */}
          <div className="hidden lg:flex items-center">
            <div className="flex items-center gap-3">
              <Link
                to="/inquiry"
                className="btn-premium text-base px-10 py-4"
              >
                Shop Now
              </Link>
              <WhatsAppLink className="flex items-center justify-center gap-2 px-4 py-4 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors duration-300 shadow-lg hover:shadow-xl">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                <span className="text-sm font-medium">WhatsApp Us</span>
              </WhatsAppLink>
            </div>
          </div>


        </div>


      </div>
    </header>
  );
};

export default Header;
